// @ts-nocheck
import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { MobileFormSheet } from '../ui/MobileFormSheet';

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: (clientId: string) => void;
};

export const MobileClientQuickForm = ({ open, onOpenChange, onCreated }: Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    client_type: 'person' as 'person' | 'company',
    cpf_cnpj: '',
    email: '',
    mobile: '',
    phone: '',
    city: '',
    state: '',
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: '',
        client_type: 'person',
        cpf_cnpj: '',
        email: '',
        mobile: '',
        phone: '',
        city: '',
        state: '',
      });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Informe o nome', variant: 'destructive' });
      return;
    }
    if (!user) return;
    setSaving(true);
    try {
      const payload: any = {
        user_id: user.id,
        name: form.name.trim(),
        client_type: form.client_type,
        cpf_cnpj: form.cpf_cnpj.trim() || null,
        email: form.email.trim() || null,
        mobile: form.mobile.trim() || null,
        phone: form.phone.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        lgpd_consent: false,
      };
      const { data, error } = await supabase
        .from('clients')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;

      toast({ title: 'Cliente criado', description: form.name });
      onOpenChange(false);
      onCreated?.(data.id);
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileFormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Novo cliente"
      description="Cadastro rápido. Complete os demais dados depois."
      footer={
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 h-12"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            className="flex-1 h-12"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Criar'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <Label className="text-xs font-semibold">Tipo</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {[
              { id: 'person', label: 'Pessoa Física' },
              { id: 'company', label: 'Pessoa Jurídica' },
            ].map((t: any) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setForm({ ...form, client_type: t.id })}
                className={`h-11 rounded-lg border text-sm font-medium transition-colors ${
                  form.client_type === t.id
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="client-name" className="text-xs font-semibold">
            {form.client_type === 'company' ? 'Razão social' : 'Nome completo'} *
          </Label>
          <Input
            id="client-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={
              form.client_type === 'company'
                ? 'Empresa LTDA'
                : 'João da Silva'
            }
            className="h-12 mt-1"
            autoComplete="name"
          />
        </div>

        <div>
          <Label htmlFor="client-doc" className="text-xs font-semibold">
            {form.client_type === 'company' ? 'CNPJ' : 'CPF'}
          </Label>
          <Input
            id="client-doc"
            value={form.cpf_cnpj}
            onChange={(e) => setForm({ ...form, cpf_cnpj: e.target.value })}
            placeholder={
              form.client_type === 'company'
                ? '00.000.000/0000-00'
                : '000.000.000-00'
            }
            inputMode="numeric"
            className="h-12 mt-1 font-mono"
          />
        </div>

        <div>
          <Label htmlFor="client-mobile" className="text-xs font-semibold">
            Celular / WhatsApp
          </Label>
          <Input
            id="client-mobile"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            placeholder="(00) 00000-0000"
            className="h-12 mt-1"
          />
        </div>

        <div>
          <Label htmlFor="client-email" className="text-xs font-semibold">
            E-mail
          </Label>
          <Input
            id="client-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="contato@exemplo.com"
            className="h-12 mt-1"
          />
        </div>

        <div className="grid grid-cols-[1fr_90px] gap-2">
          <div>
            <Label htmlFor="client-city" className="text-xs font-semibold">
              Cidade
            </Label>
            <Input
              id="client-city"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="h-12 mt-1"
            />
          </div>
          <div>
            <Label htmlFor="client-state" className="text-xs font-semibold">
              UF
            </Label>
            <Input
              id="client-state"
              value={form.state}
              onChange={(e) =>
                setForm({ ...form, state: e.target.value.toUpperCase() })
              }
              maxLength={2}
              className="h-12 mt-1 text-center font-semibold uppercase"
            />
          </div>
        </div>
      </div>
    </MobileFormSheet>
  );
};

export default MobileClientQuickForm;
